import { ReactNode } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { parseISO } from 'date-fns';

import { ActivityOfType } from '@/components/activities/Activity';
import {
  BaseActivity,
  BaseActivityArticleName,
  BaseActivityText,
} from '@/components/activities/BaseActivity/BaseActivity';
import InlineText from '@/design_system/Typography/InlineText';
import IconEdit from '@/icons/Edit.svg';
import { useDatabaseTranslation } from '@/services/i18n';
import { formatDate } from '@/utils/date';
import { formatCurrencyRange, formatWeight } from '@/utils/number';

export const ArticlePropertyUpdatedActivity = ({
  activity,
  displayArticleName,
}: {
  activity: ActivityOfType<'article_property_updated'>;
  displayArticleName: boolean;
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  let valueAfterTheChange: ReactNode;
  let articlePropertyUpdatedMessage: string | undefined;

  switch (activity.data.property) {
    case 'color': {
      articlePropertyUpdatedMessage = t({
        id: 'activities.article_property_updated.data.color',
        message: 'the color',
      });

      valueAfterTheChange = activity.data.after ? _db(activity.data.after) : undefined;
      break;
    }

    case 'size': {
      articlePropertyUpdatedMessage = t({
        id: 'activities.article_property_updated.data.size',
        message: 'the size',
      });

      valueAfterTheChange = activity.data.after ? _db(activity.data.after) : undefined;
      break;
    }

    case 'brand': {
      articlePropertyUpdatedMessage = t({
        id: 'activities.article_property_updated.data.brand',
        message: 'the brand',
      });
      valueAfterTheChange = activity.data.after?.otherBrandName ?? activity.data.after?.name;
      break;
    }

    case 'priceManualDiscount': {
      articlePropertyUpdatedMessage = t({
        id: 'activities.article_property_updated.price_manual_discount',
        message: 'the discount applied on client price',
      });
      valueAfterTheChange = activity.data.after ? `-${activity.data.after}%` : '0%';
      break;
    }

    case 'purchaseDate': {
      articlePropertyUpdatedMessage = t({
        id: 'activities.article_property_updated.purchase_date',
        message: 'the purchase date',
      });
      valueAfterTheChange = activity.data.after
        ? formatDate(parseISO(activity.data.after), {
            dateStyle: 'medium',
          })
        : '';
      break;
    }

    case 'warranty': {
      articlePropertyUpdatedMessage = t({
        id: 'activities.article_property_updated.warranty',
        message: 'the warranty',
      });
      valueAfterTheChange = activity.data.after
        ? t({ id: 'activities.article_property_updated.warranty.yes', message: 'Under warranty' })
        : t({ id: 'activities.article_property_updated.warranty.no', message: 'Out-of-Warranty' });

      break;
    }

    case 'uniqueArticle': {
      articlePropertyUpdatedMessage = t({
        id: 'activities.article_property_updated.unique_article',
        message: 'the serial number',
      });
      valueAfterTheChange = activity.data.after?.serialNumber;
      break;
    }

    case 'product.externalId': {
      articlePropertyUpdatedMessage = t({
        id: 'activities.article_property_updated.product_reference',
        message: 'the product',
      });
      valueAfterTheChange = activity.data.after
        ? `${activity.data.after.externalId} - ${activity.data.after.name}`
        : null;
      break;
    }

    case 'weight': {
      articlePropertyUpdatedMessage = t({
        id: 'activities.article_property_updated.weight',
        message: 'the weight',
      });
      valueAfterTheChange = activity.data.after
        ? formatWeight(activity.data.after, {
            unitDisplay: 'long',
          })
        : null;
      break;
    }

    case 'productAttributeValue':
      articlePropertyUpdatedMessage = activity.data.after?.attributeName
        ? _db(activity.data.after.attributeName)
        : activity.data.before?.attributeName
          ? _db(activity.data.before.attributeName)
          : undefined;

      valueAfterTheChange = activity.data.after?.value ? _db(activity.data.after.value) : undefined;
      break;
    case 'data.estimatedValue':
      articlePropertyUpdatedMessage = t({
        id: 'activities.article_property_updated.estimated_value',
        message: 'the estimated value',
      });

      if (!activity.data.after) {
        valueAfterTheChange = null;
        break;
      }

      if (activity.data.after === 'unknown') {
        valueAfterTheChange = t({
          id: 'activities.article_property_updated.estimated_value.unknown',
          message: 'Unknown',
        });
        break;
      }

      valueAfterTheChange = formatCurrencyRange(activity.data.after);

      break;

    default:
      return null;
  }

  const creatorName = activity.creator?.name;

  return (
    <BaseActivity
      key={activity.id}
      createdAt={activity.createdAt}
      icon={<IconEdit />}
      message={
        valueAfterTheChange ? (
          <>
            <BaseActivityText>
              <Trans id="activities.article_property_updated.message">
                <strong>{creatorName}</strong> edited {articlePropertyUpdatedMessage} of the item
              </Trans>
            </BaseActivityText>
            <BaseActivityArticleName activity={activity} displayArticleName={displayArticleName} />

            <InlineText weight="medium">
              <Trans id="activities.article_property_updated.separator">:</Trans>{' '}
              {valueAfterTheChange}
            </InlineText>
          </>
        ) : (
          <>
            <BaseActivityText>
              <Trans id="activities.article_property_updated.removed.message">
                <strong>{creatorName}</strong> removed {articlePropertyUpdatedMessage} of the item
              </Trans>
            </BaseActivityText>
            <BaseActivityArticleName activity={activity} displayArticleName={displayArticleName} />
          </>
        )
      }
    />
  );
};
